import React, { FunctionComponent, useContext } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppContext } from "../../../app/store";
import {
  Plan,
  PackageServices,
  DetailItemProps,
  AdditionalSupplementalBenefitCoverage,
  PlanCategoryType,
  PlanDetailsSection,
} from "../../../@types";
import {
  ItemIterator,
  CheckmarkIcon,
  Cell,
  Spacer,
  PlanDetailsHashLink,
} from "../../../components";
import {
  $$,
  isAvailable as iA,
  useTranslate,
  getBenefitCost,
  planDataUtils,
  isMedicareAdvantageType,
  shouldSuppressPlanBenefits,
  isMaPlanType,
  getAdditionalBenefit,
} from "../../../helpers";
import CDI from "./ComparisonDetailItem";
import HeaderRow from "../HeaderRow";
import { calculateYourResponsibility } from "../../../helpers/planHelpers";

const PlanOverview: FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const { mctCurrentPlanYear } = useFlags();
  const { package_services } = plan;
  const services = package_services || ({} as PackageServices);
  const suppressed = shouldSuppressPlanBenefits(plan);
  const ComparisonDetailItem: React.FunctionComponent<DetailItemProps> = props => {
    const extendedProps = { ...props, suppressed };
    return <CDI {...extendedProps} />;
  };

  const exclusionOr = (nonExclusionText: string): string => {
    return shouldSuppressPlanBenefits(plan)
      ? t("suppressed_plan_benefit")
      : nonExclusionText;
  };

  const PDPOverview = (
    <div className="columnSection">
      <div className="planCompare__cell">
        <ComparisonDetailItem
          detail={$$(plan.calculated_monthly_premium)}
          label={t("total")}
        />
      </div>

      <div className="planCompare__cell">
        <ComparisonDetailItem
          detail={planDataUtils.getAnnualDrugDeductible(
            state,
            mctCurrentPlanYear,
            plan
          )}
          label={t("pdp.plan_details.yearly_drug_deductible")}
        />
      </div>
    </div>
  );

  const mapdPremiumItems = [
    {
      detail: $$(plan.partc_premium),
      label: t("medicare_advantage.plan_details.health_premium"),
    },
    {
      detail: $$(
        planDataUtils.getPartDPremiumOrLIS(state, mctCurrentPlanYear, plan)
      ),
      label: t("medicare_advantage.plan_details.drug_premium"),
    },
    {
      detail: $$(planDataUtils.getPartBPremium(plan)),
      label: t("plan_card.partb_premium"),
    },
  ];

  const maPremiumItems = mapdPremiumItems.filter(
    item => item.label !== t("medicare_advantage.plan_details.drug_premium")
  );

  const mapdDeductibleItems = [
    {
      detail: plan.annual_deductible,
      label: t("plan_terms.health_plan_deductible"),
    },
    {
      detail: planDataUtils.getAnnualDrugDeductible(
        state,
        mctCurrentPlanYear,
        plan
      ),
      label: t("plan_terms.drug_plan_deductible"),
    },
  ];

  let maDeductibleItems = [mapdDeductibleItems[0]];

  if (plan.category === PlanCategoryType.MSA) {
    maDeductibleItems = [
      ...maDeductibleItems,
      {
        detail: $$(plan.msa_annual_deposit),
        label: t("msa_plan.yearly_deposit"),
      },
      {
        detail: $$(
          calculateYourResponsibility(
            plan.annual_deductible,
            plan.msa_annual_deposit
          )
        ),
        label: t("msa_plan.your_responsibility"),
      },
    ];
  }

  const moreBenefits = [
    {
      label: t("more_benefits.over_the_counter_drugs"),
      category: "otc_items",
      benefit: "otc_items",
    },
    {
      label: t("more_benefits.in_home_support"),
      category: "defined_supplemental_benefits",
      benefit: "in_home_support_services",
    },
    {
      label: t("more_benefits.worldwide_emergency"),
      category: "worldwide_emergency_transportation",
      benefit: "worldwide_emergency_coverage",
    },
    {
      label: t("more_benefits.worldwide_urgent"),
      category: "worldwide_emergency_transportation",
      benefit: "worldwide_urgent_care",
    },
    {
      label: t("more_benefits.response_device"),
      category: "defined_supplemental_benefits",
      benefit: "personal_emergency_response_sys_pers",
    },
    {
      label: t("more_benefits.chiropractic_service"),
      category: "chiropractic_services",
      benefit: "routine_care",
    },
    {
      label: t("more_benefits.bathroom_safety_devices"),
      category: "defined_supplemental_benefits",
      benefit: "bathroom_safety_devices",
    },
    {
      label: t("more_benefits.meals"),
      category: "meal_benefit",
      benefit: "meal_benefit",
    },
    {
      label: t("more_benefits.annual_physical"),
      category: "annual_physical_exam",
      benefit: "annual_physical_exam",
    },
    {
      label: t("more_benefits.telehealth"),
      category: "additional_telehealth_services",
      benefit: "medicare_covered_visits",
    },
  ];

  const benefitServices = [
    {
      label: t("plan_terms.comprehensive_dental.endodontics"),
      category: "comprehensive_dental",
      service: "endodontics",
    },
    {
      label: t("plan_terms.comprehensive_dental.periodontics"),
      category: "comprehensive_dental",
      service: "periodontics",
    },
    {
      label: t("plan_terms.comprehensive_dental.extractions"),
      category: "comprehensive_dental",
      service: "extractions",
    },
  ];

  const MAPDOverview = (
    <>
      <div className="columnSection">
        <div className="planCompare__cell">
          <ItemIterator Component={ComparisonDetailItem}>
            {isMaPlanType(plan.plan_type) ? maPremiumItems : mapdPremiumItems}
          </ItemIterator>
        </div>

        <div className="planCompare__cell">
          <ItemIterator Component={ComparisonDetailItem}>
            {isMaPlanType(plan.plan_type)
              ? maDeductibleItems
              : mapdDeductibleItems}
          </ItemIterator>
        </div>

        <div className="planCompare__cell">
          <ComparisonDetailItem
            mobileLabelOnly
            detail={exclusionOr(
              getBenefitCost(t, { plan, benefit: "maximum_oopc" })
            )}
            label={t("plan_terms.oopc_maximum")}
          />
        </div>
      </div>

      <Cell headerRow filler>
        <HeaderRow text="plan_terms.plan_features" />
        <Spacer />
      </Cell>
      <div className="columnSection">
        <div className="planCompare__cell">
          {shouldSuppressPlanBenefits(plan) ? (
            t("suppressed_plan_benefit")
          ) : (
            <>
              <ul className="planCompare__benefitList">
                <li className="planCompare__checkItem">
                  <CheckmarkIcon checked={iA(services.vision_services)} />
                  <label>{t("plan_terms.vision")}</label>
                </li>

                <li className="planCompare__checkItem">
                  <CheckmarkIcon checked={iA(services.dental_services)} />
                  <label>{t("plan_terms.dental")}</label>
                </li>

                <li className="planCompare__checkItem">
                  <CheckmarkIcon checked={iA(services.hearing_services)} />
                  <label>{t("plan_terms.hearing")}</label>
                </li>

                <li className="planCompare__checkItem">
                  <CheckmarkIcon
                    checked={
                      getBenefitCost(t, { benefit: "transportation", plan }) !==
                      t("plan_terms.not_covered")
                    }
                  />
                  <label>{t("plan_terms.transportation")}</label>
                </li>

                <li className="planCompare__checkItem">
                  <CheckmarkIcon checked={plan.silver_sneakers} />
                  <label>{t("plan_terms.fitness_benefits")}</label>
                </li>

                <li className="planCompare__checkItem">
                  <CheckmarkIcon checked={plan.worldwide_emergency} />
                  <label>{t("more_benefits.worldwide_emergency")}</label>
                </li>

                {moreBenefits.map(
                  (benefit: {
                    label: string;
                    category: string;
                    benefit: string;
                  }) => {
                    const benefitDetail = getAdditionalBenefit(
                      plan,
                      "other_benefits",
                      benefit.category,
                      benefit.benefit
                    );
                    if (benefitDetail) {
                      return (
                        <li
                          key={benefit.benefit}
                          className="planCompare__checkItem"
                        >
                          <CheckmarkIcon
                            checked={
                              benefitDetail.coverage ===
                              AdditionalSupplementalBenefitCoverage.SB_COVERAGE_SOME_COVERAGE
                            }
                          />
                          <label>{benefit.label}</label>
                        </li>
                      );
                    }
                    return null;
                  }
                )}

                {benefitServices.map(
                  (service: {
                    label: string;
                    category: string;
                    service: string;
                  }) => {
                    const benefitDetail = getBenefitCost(t, {
                      plan,
                      benefit: service.category,
                      service: service.service,
                    });
                    if (benefitDetail) {
                      return (
                        <li
                          key={service.service}
                          className="planCompare__checkItem"
                        >
                          <CheckmarkIcon
                            checked={
                              benefitDetail !== t("plan_terms.not_covered")
                            }
                          />
                          <label>{service.label}</label>
                        </li>
                      );
                    }
                    return null;
                  }
                )}
              </ul>
              <div className="planCompare__checkitem">
                <PlanDetailsHashLink
                  label={t("plan_terms.view_additional_benefits")}
                  plan={plan}
                  section={PlanDetailsSection.EXTRA_BENEFITS}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );

  return isMedicareAdvantageType(plan.plan_type) ? MAPDOverview : PDPOverview;
};

export default PlanOverview;
